// src/pages/Products.jsx
import { useState, useEffect, useMemo } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import styled, { createGlobalStyle, keyframes, css } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

/* ── Global ─────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Figtree:ital,wght@0,400;0,500;0,600;1,400&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f7f8fc; }
  ::selection { background: #1a56ff22; }
`;

const C = {
  bg: "#f7f8fc",
  white: "#ffffff",
  blue: "#1a56ff",
  blueMid: "#3d6fff",
  blueSoft: "#eef2ff",
  blueDeep: "#0f3acc",
  text: "#0d0d0d",
  sub: "#6b7280",
  border: "#e8eaed",
  surface: "#f0f1f5",
};

/* ── Shimmer ────────────────────────────────────────────────── */
const shimmer = keyframes`
  0%   { background-position: -700px 0; }
  100% { background-position:  700px 0; }
`;
const skeletonAnim = css`
  background: linear-gradient(90deg, #ebebeb 25%, #f5f5f5 50%, #ebebeb 75%);
  background-size: 700px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

/* ── Page ───────────────────────────────────────────────────── */
const Page = styled.div`
  min-height: 100vh;
  background: ${C.bg};
  font-family: "Figtree", sans-serif;
`;

const Wrap = styled.div`
  max-width: 1360px;
  margin: 0 auto;
  padding: 0 48px;
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

/* ── Toolbar ────────────────────────────────────────────────── */
const Toolbar = styled.div`
  padding: 32px 0 28px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

/* base for all filter controls */
const controlBase = css`
  font-family: "Figtree", sans-serif;
  font-size: 13.5px;
  font-weight: 500;
  color: ${C.text};
  background: ${C.white};
  border: 1.5px solid ${C.border};
  border-radius: 100px;
  outline: none;
  transition:
    border-color 0.18s,
    box-shadow 0.18s;
  &:focus {
    border-color: ${C.blue};
    box-shadow: 0 0 0 3px #1a56ff15;
  }
  &::placeholder {
    color: #b8bcc8;
    font-weight: 400;
  }
`;

const SearchWrap = styled.div`
  position: relative;
  svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #b8bcc8;
    pointer-events: none;
  }
`;

const SearchInput = styled.input`
  ${controlBase}
  padding: 10px 18px 10px 38px;
  width: 260px;
  font-family: "Montserrat", sans-serif;
  @media (max-width: 500px) {
    width: 100%;
  }
`;

const BrandSelect = styled.select`
  ${controlBase}
  padding: 10px 36px 10px 16px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23b8bcc8' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  background-color: ${C.white};
  font-family: "Montserrat", sans-serif;
`;

const PriceWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${C.white};
  border: 1.5px solid ${C.border};
  border-radius: 100px;
  padding: 10px 16px;
  transition:
    border-color 0.18s,
    box-shadow 0.18s;
  &:focus-within {
    border-color: ${C.blue};
    box-shadow: 0 0 0 3px #1a56ff15;
  }
`;

const PriceInput = styled.input`
  width: 60px;
  border: none;
  background: transparent;
  font-family: "Montserrat", sans-serif;
  font-size: 13.5px;
  font-weight: 500;
  color: ${C.text};
  outline: none;
  &::placeholder {
    color: #b8bcc8;
    font-weight: 400;
  }
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    appearance: none;
  }
`;

const PriceDash = styled.span`
  color: #d1d5db;
  font-size: 12px;
  user-select: none;
`;

const PriceDollar = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #b8bcc8;
`;

const ClearPill = styled(motion.button)`
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${C.blue};
  background: ${C.blueSoft};
  border: none;
  border-radius: 100px;
  padding: 10px 18px;
  cursor: pointer;
  transition: background 0.18s;
  &:hover {
    background: #dde5ff;
  }
`;

/* ── Stats row ──────────────────────────────────────────────── */
const StatsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 20px;
  margin-bottom: 28px;
  border-bottom: 1.5px solid ${C.border};
`;

const ResultCount = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: ${C.sub};
  letter-spacing: 0.01em;
  font-family: "Montserrat", sans-serif;

  strong {
    color: ${C.text};
    font-weight: 700;
  }
`;

const SortSelect = styled.select`
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${C.sub};
  background: transparent;
  border: none;
  outline: none;
  cursor: pointer;
  appearance: none;
  padding-right: 18px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0 center;
`;

/* ── Grid ───────────────────────────────────────────────────── */
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(272px, 1fr));
  gap: 16px;

  @media (max-width: 580px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
`;

/* ── Card ───────────────────────────────────────────────────── */
const Card = styled(motion.article)`
  background: ${C.white};
  border-radius: 20px;
  overflow: hidden;
  border: 1.5px solid transparent;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  transition:
    border-color 0.2s,
    box-shadow 0.2s,
    transform 0.2s;
  cursor: default;

  &:hover {
    border-color: ${C.blue};
    box-shadow: 0 8px 32px #1a56ff12;
    transform: translateY(-2px);
  }
`;

const ImgWrap = styled.div`
  width: 100%;
  /* aspect-ratio: 4 / 3; */
  background: ${C.surface};
  overflow: hidden;
  max-height: 330px;
  position: relative;
  @media (max-width: 500px) {
  }
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background-color: white;
    transition: transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
    display: block;
    padding-top: 20px;
  }

  ${Card}:hover & img {
    transform: scale(1.07);
  }
`;

const BrandBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${C.blue};
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
`;

const CardBody = styled.div`
  padding: 16px 20px 20px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  @media (max-width: 500px) {
    flex-direction: column;
    align-items: self-start;
  }
`;

const CardName = styled.h3`
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #5e5a5a;
  line-height: 1.35;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-transform: capitalize;
`;

const PriceTag = styled.div`
  flex-shrink: 0;
  background: #e2e2e2;
  border-radius: 10px;
  padding: 6px 12px;
  font-family: "Syne", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #4d4b4b;
  letter-spacing: -0.02em;
  white-space: nowrap;
  @media (max-width: 500px) {
    background: #e2e2e284;
    padding: 6px 10px;
  }
`;

/* ── Skeleton ───────────────────────────────────────────────── */
const SkeletonCard = styled.div`
  background: ${C.white};
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

  .s-img {
    width: 100%;
    aspect-ratio: 4/3;
    ${skeletonAnim}
  }
  .s-body {
    padding: 16px 20px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }
  .s-line {
    height: 13px;
    border-radius: 6px;
    flex: 1;
    ${skeletonAnim}
  }
  .s-price {
    width: 64px;
    height: 30px;
    border-radius: 8px;
    flex-shrink: 0;
    ${skeletonAnim}
  }
`;

/* ── Empty ──────────────────────────────────────────────────── */
const Empty = styled.div`
  grid-column: 1/-1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 96px 20px;
  gap: 10px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 18px;
  background: ${C.blueSoft};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 4px;
`;

const EmptyTitle = styled.p`
  font-family: "Syne", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${C.text};
`;

const EmptySub = styled.p`
  font-size: 13px;
  color: ${C.sub};
`;

/* ── Component ──────────────────────────────────────────────── */
export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("all");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("default");

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "products"), (snap) => {
      setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const brands = useMemo(() => {
    const s = new Set(products.map((p) => p.brand).filter(Boolean));
    return ["all", ...Array.from(s).sort()];
  }, [products]);

  const filtered = useMemo(() => {
    const min = minPrice !== "" ? parseFloat(minPrice) : null;
    const max = maxPrice !== "" ? parseFloat(maxPrice) : null;
    const q = search.toLowerCase();

    let list = products.filter((p) => {
      if (
        q &&
        !p.name?.toLowerCase().includes(q) &&
        !p.brand?.toLowerCase().includes(q)
      )
        return false;
      if (brand !== "all" && p.brand !== brand) return false;
      if (min !== null && p.price < min) return false;
      if (max !== null && p.price > max) return false;
      return true;
    });

    if (sort === "low") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "high") list = [...list].sort((a, b) => b.price - a.price);
    if (sort === "az")
      list = [...list].sort((a, b) => a.name?.localeCompare(b.name));

    return list;
  }, [products, search, brand, minPrice, maxPrice, sort]);

  const hasFilters =
    search || brand !== "all" || minPrice !== "" || maxPrice !== "";
  const clearAll = () => {
    setSearch("");
    setBrand("all");
    setMinPrice("");
    setMaxPrice("");
  };

  return (
    <>
      <GlobalStyle />
      <Page>
        <Wrap>
          {/* ── Toolbar ── */}
          <Toolbar>
            <SearchWrap>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <SearchInput
                placeholder="Kërko produkte…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchWrap>

            <BrandSelect
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
            >
              {brands.map((b) => (
                <option key={b} value={b}>
                  {b === "all" ? "Të gjitha brendet" : b}
                </option>
              ))}
            </BrandSelect>

            <PriceWrap>
              <PriceDollar>€</PriceDollar>
              <PriceInput
                type="number"
                min="0"
                placeholder="Min"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
              />
              <PriceDash>—</PriceDash>
              <PriceInput
                type="number"
                min="0"
                placeholder="Max"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
              />
            </PriceWrap>

            <AnimatePresence>
              {hasFilters && (
                <ClearPill
                  onClick={clearAll}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.15 }}
                >
                  ✕ Clear
                </ClearPill>
              )}
            </AnimatePresence>
          </Toolbar>

          {/* ── Stats row ── */}
          {!loading && (
            <StatsRow>
              <ResultCount>
                Showing <strong>{filtered.length}</strong>{" "}
                {filtered.length === 1 ? "product" : "products"}
              </ResultCount>
              <SortSelect
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value="default">Rendit: Nga Relevanca</option>
                <option value="low">Cmimi: Ulët në të lartë </option>
                <option value="high">Cmimi: Të lartë në të ulët</option>
                <option value="az">Emri: A → Z</option>
              </SortSelect>
            </StatsRow>
          )}

          {/* ── Grid ── */}
          <Grid>
            {loading &&
              Array.from({ length: 8 }).map((_, i) => (
                <SkeletonCard key={i}>
                  <div className="s-img" />
                  <div className="s-body">
                    <div className="s-line" />
                    <div className="s-price" />
                  </div>
                </SkeletonCard>
              ))}

            <AnimatePresence mode="popLayout">
              {!loading && filtered.length === 0 && (
                <Empty key="empty">
                  <EmptyIcon>🔍</EmptyIcon>
                  <EmptyTitle>No products found</EmptyTitle>
                  <EmptySub>
                    Try adjusting your search or clearing filters
                  </EmptySub>
                </Empty>
              )}

              {!loading &&
                filtered.map((p, i) => (
                  <Card
                    key={p.id}
                    layout
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{
                      duration: 0.26,
                      delay: Math.min(i * 0.04, 0.3),
                    }}
                  >
                    <Link to={`/produktet/${p.id}`} className="link-styles">
                      <ImgWrap>
                        <img src={p.imageUrl} alt={p.name} loading="lazy" />
                        <BrandBadge>{p.brand}</BrandBadge>
                      </ImgWrap>
                      <CardBody>
                        <CardName>{p.name}</CardName>
                        <PriceTag>{Number(p.price).toFixed(2)}€</PriceTag>
                      </CardBody>
                    </Link>
                  </Card>
                ))}
            </AnimatePresence>
          </Grid>

          {/* bottom padding */}
          <div style={{ height: 80 }} />
        </Wrap>
      </Page>
    </>
  );
}
